import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getEmployeeDates } from "../../../core/data/redux/milestonesSlice";
import { all_routes } from "../../router/all_routes";

const BirthdayAnniversaries = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const { employeeDates, loading } = useSelector((state: any) => state.milestonesState);

  const [activeTab, setActiveTab] = useState("Birthday");
  const [selectedMonthYear, setSelectedMonthYear] = useState("");

  const currentYear = 2026;

  // Month selector (Jan 2026 to Dec 2026)
  const monthSelector = useMemo(() => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYear, i, 1);
      months.push({
        label: date.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
        value: `${currentYear}-${(i + 1).toString().padStart(2, "0")}`,
      });
    }
    return months;
  }, [currentYear]);

  // Default selection
  useEffect(() => {
    if (!selectedMonthYear) {
      const now = new Date();
      const currentVal = `${currentYear}-${(now.getMonth() + 1).toString().padStart(2, "0")}`;
      setSelectedMonthYear(currentVal);
    }
  }, [selectedMonthYear, currentYear]);

  // Fetch API
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      const empId = user?.emp_id || user?.employee_id || "";
      dispatch(getEmployeeDates({ empId }));
    }
  }, [dispatch]);

  // Resilient data extraction
  const milestonesData = useMemo(() => {
    let list = [];
    if (Array.isArray(employeeDates?.data)) list = employeeDates.data;
    else if (Array.isArray(employeeDates?.data?.data)) list = employeeDates.data.data;
    else if (Array.isArray(employeeDates?.data?.data?.data)) list = employeeDates.data.data.data;
    return list;
  }, [employeeDates]);

  // Filter & Map
  const filteredMilestones = useMemo(() => {
    if (!selectedMonthYear) return [];
    const selectedMonth = parseInt(selectedMonthYear.split("-")[1]);

    return milestonesData
      .map((item: any) => {
        const dateStr = activeTab === "Birthday" ? item.BirthDate : item.AnniversaryDate;
        if (!dateStr) return null;
        const parts = dateStr.split("-");
        if (parts.length !== 3) return null;
        const [day, month] = parts;
        const dayNum = parseInt(day);
        const monthNum = parseInt(month);
        if (!dayNum || !monthNum) return null;

        return {
          ...item,
          dayNum,
          monthIndex: monthNum - 1,
          displayDay: day.padStart(2, "0"),
          displayMonth: new Date(currentYear, monthNum - 1).toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
        };
      })
      .filter((item: any) => item && (item.monthIndex + 1) === selectedMonth)
      .sort((a: any, b: any) => a.dayNum - b.dayNum);
  }, [milestonesData, activeTab, selectedMonthYear, currentYear]);

  return (
    <div className="page-wrapper bg-white">
      <style>{`
        .nav-tabs-standard .nav-link { border: none; color: #6c757d; font-weight: 600; padding: 1rem 2rem; position: relative; background: transparent; }
        .nav-tabs-standard .nav-link.active { color: #B71C1C; }
        .nav-tabs-standard .nav-link.active::after { content: ''; position: absolute; bottom: 0; left: 20%; right: 20%; height: 3px; background: #B71C1C; border-radius: 3px; }
        .month-btn { border: 1px solid #e9ecef !important; transition: all 0.2s; background: #fff; color: #495057; }
        .month-btn.active { background: #FDECEA !important; border-color: #F8D7DA !important; color: #B71C1C !important; font-weight: 700; transform: scale(1.05); }
        .milestone-item { transition: background 0.2s; border-bottom: 1px solid #f1f3f5 !important; }
        .milestone-item:hover { background: #fcfcfc; }
        .date-badge-soft { background: #FDECEA; color: #B71C1C; width: 50px; height: 50px; border-radius: 10px; display: flex; flex-direction: column; justify-content: center; align-items: center; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      <div className="content">
        {/* Header */}
        <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
          <Link to={routes.employeeDashboard} className="text-dark me-2">
            <i className="ti ti-arrow-left fs-20"></i>
          </Link>
          <h4 className="fw-bold mb-0">Birthdays & Anniversaries</h4>
        </div>

        {/* Standard Tabs */}
        <div className="mb-4">
          <ul className="nav nav-tabs-standard d-flex border-bottom">
            <li className="nav-item flex-grow-1 text-center">
              <button className={`nav-link w-100 ${activeTab === "Birthday" ? "active" : ""}`} onClick={() => setActiveTab("Birthday")}>
                <i className="ti ti-cake me-2"></i>Birthdays
              </button>
            </li>
            <li className="nav-item flex-grow-1 text-center">
              <button className={`nav-link w-100 ${activeTab === "Anniversary" ? "active" : ""}`} onClick={() => setActiveTab("Anniversary")}>
                <i className="ti ti-award me-2"></i>Anniversaries
              </button>
            </li>
          </ul>
        </div>

        {/* Month Selector */}
        <div className="d-flex overflow-auto pb-4 no-scrollbar gap-2">
          {monthSelector.map((item, index) => (
            <button
              key={index}
              className={`btn month-btn rounded-pill px-4 ${selectedMonthYear === item.value ? "active" : ""}`}
              style={{ minWidth: "90px" }}
              onClick={() => setSelectedMonthYear(item.value)}
            >
              <div style={{ fontSize: '12px' }}>{item.label.split(" ")[0]}</div>
              <div className="opacity-50" style={{ fontSize: '10px' }}>{item.label.split(" ")[1]}</div>
            </button>
          ))}
        </div>

        {/* List View - More Standard */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
          <div className="card-body p-0">
            {loading === "pending" ? (
              <div className="text-center py-5"><div className="spinner-border text-muted" /></div>
            ) : filteredMilestones.length > 0 ? (
              filteredMilestones.map((item: any, index: number) => (
                <div key={index} className="milestone-item p-3 d-flex align-items-center">
                  <div className="date-badge-soft me-3 shadow-sm">
                    <span className="fw-bold h5 mb-0">{item.displayDay}</span>
                    <span className="fw-bold text-uppercase" style={{ fontSize: '9px' }}>{item.displayMonth}</span>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '15px' }}>{item.EmployeeName}</h6>
                    <small className="text-muted">Emp ID: {item.ReporteeEmpID}</small>
                  </div>
                  <div className="text-muted opacity-50">
                    <i className={`ti ${activeTab === 'Birthday' ? 'ti-cake' : 'ti-rings-military'} fs-20 text-danger opacity-25`}></i>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-5">
                <p className="text-muted mb-0">No entries found for this period.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthdayAnniversaries;
